import { useMachine } from '@xstate/react';
import * as React from 'react';
import { Badge, Check, Gear, Mag, X } from './Icons';
import './LoadingScreen.css';
import { loaderMachine } from './machine';

type MessageConfig = {
  message: string;
  meta: {
    to: number;
    duration: string;
  };
};

interface LoadingScreenProps {
  /** callback function to be executed when loading animation finishes */
  onDone: () => void;
  /** determines outcome presentation, e.g. should it finsh with green check (success) or red 'x' (error) */
  status?: 'success' | 'error' | 'loading';
  /** amount of time to wait before calling onDone
   * @param doneAfter [doneAfter=250]
   */
  doneAfter?: number;
  /** array of 2 messages displayed as loading animation progresses */
  messages?: [message?: MessageConfig, messageTakingLonger?: MessageConfig];
}

/** switch between 1 and 0 opacity */
const flipOpacity = (shouldBeVisible: boolean) => (shouldBeVisible ? 1 : 0);

export const LoadingScreen = (props: LoadingScreenProps) => {
  const [state, send] = useMachine(loaderMachine, {
    actions: { onDone: props.onDone },
    delays: { DONE_AFTER: props.doneAfter || 250 },
  });

  // squish all the meta properties into one object
  const meta: any = Object.values(state.meta || {}).reduce(
    (acc, curr) => ({
      ...(acc as any),
      ...(curr as any),
    }),
    {}
  );

  React.useEffect(() => {
    if (props.status === 'success') {
      send('FINISH_SUCCESS');
    }
    if (props.status === 'error') {
      send('FINISH_ERROR');
    }
  }, [props.status]);

  const config = props.messages;
  // "meta" properties need to be static, but bc we want to configure some properties dyanamically
  // we create these variable which are set to the dynamic prop value if it exists, and fallback to the static meta property.
  const strokeDashoffset = config?.[meta.messageIndex]?.meta?.to || meta.to;
  const duration = config?.[meta.messageIndex]?.meta?.duration || meta.duration;
  const message = config?.[meta.messageIndex]?.message;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-labelledby="loading-screen"
      className="flex flex-col items-center justify-center gap-24"
    >
      {message && (
        <div className="sr-only" id="loading-screen">
          {message}
        </div>
      )}
      <div className="icon-122">
        <svg
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 80 80"
          xmlSpace="preserve"
          aria-hidden={true}
        >
          <g className="icon">
            <g transform="translate(20,20)">
              <Badge
                className="text-gray-darker"
                style={{
                  opacity: flipOpacity(state.hasTag('badge')),
                }}
              />
            </g>
            <g transform="translate(16,16)">
              <Mag
                className="text-gray-darker"
                style={{
                  opacity: flipOpacity(state.hasTag('mag')),
                }}
              />
            </g>
            <g
              transform="translate(17,17)"
              className={state.matches({ loading: 'takingLonger' }) ? 'animate-pulse' : ''}
            >
              <Gear
                className="text-gray-darker"
                style={{
                  opacity: flipOpacity(state.hasTag('gear')),
                }}
              />
            </g>
            <g transform="translate(20,20)">
              <Check
                className="text-green"
                style={{
                  opacity: flipOpacity(state.hasTag('check')),
                }}
              />
            </g>
            <g transform="translate(20,20)">
              <X
                className="text-red"
                style={{
                  opacity: flipOpacity(state.hasTag('x')),
                }}
              />
            </g>
          </g>
          <circle
            className="text-gray"
            cx="40"
            cy="40"
            r="36"
            fill="transparent"
            stroke="currentColor"
            strokeWidth="4"
          />
          <circle
            className={state.hasTag('red') ? 'text-red' : 'text-green'}
            // transform - move the origin of animation start to 12 oclock
            transform="rotate(-90 40 40)"
            cx="40"
            cy="40"
            r="36"
            fill="transparent"
            stroke="currentColor"
            strokeWidth="4"
            strokeDasharray={227}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: `all ${duration || '500ms'} ease-in-out` }}
            onTransitionEnd={event => {
              if (event.propertyName === 'stroke-dashoffset') {
                send({ type: 'NEXT' });
              }
            }}
          />
        </svg>
      </div>
      {message && <p className="text-blue text-md loading">{message}</p>}
    </div>
  );
};
